import { Button, CircularProgress, Paper, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { styled } from "@mui/material/styles";
import { get, includes, orderBy } from "lodash";
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import StatusChip from "../../../common/js/components/StatusChip";
import { Add, DownChevron, Sorty, StepperIcon, StepperIconComplete, UpChevron } from "../../../common/js/icons";
import { isBrokerOnDeal, isBrokerOnListing, toDollarAmountAcceptZero } from "../utils/operations";

const StyledLb = styled(Link)({
    textDecoration: "none",
});

const StyledProgressDiv = styled("div")({
    textAlign: "center",
    width: "5em",
    display: "inline-block",
});

const StyledSortable = styled("span")({
    paddingRight: "5px",
    paddingBottom: "3px",
});

const StyledIdLink = styled(Link)({
    textDecoration: "none",
    color: "#2C99D2",
    fontWeight: 600,
    "&:hover": {
        cursor: "pointer",
        textDecoration: "underline",
    },
});

const StyledDivHeader = styled("div")({
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    maxwidth: "41.666667%",
    flexBasis: "41.666667%",
    flexGrow: 0,
    boxSizing: "border-box",
    margin: "-8px",
    padding: "12px",
});

const StyledDivHeaderContainer = styled("div")({
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    margin: "-12px",
    boxSizing: "border-box",
});

const DealSummaryVoucherTable = (props) => {
    const {
        userProfile,
        columnToSort,
        handleSort,
        isLoadingContent,
        loading,
        sortDirection,
        values,
        role,
        startVoucher,
        addVoucherInstallment,
        compAction,
        voucherAction,
        handleStartReviewingAsCorporateDataAndRedirect,
    } = props;

    const navigate = useNavigate();

    const showVoucherView = () => {
        const show =
            includes(["accounting", "evp", "dataAdmin", "dataEntry"], userProfile.role) ||
            (includes(["portlandBrokerAdmin"], userProfile.role) &&
                (values?.adminReviewType === "PORTLAND" ||
                    values?.vouchers?.some((voucher) => voucher.adminReviewType === "PORTLAND"))) ||
            (includes(["arizonaBrokerAdmin"], userProfile.role) && values?.adminReviewType === "ARIZONA") ||
            userProfile.username === values?.deal?.auditCreatedBy ||
            isBrokerOnListing(values?.listing?.listingBrokers ?? [], userProfile.personKey) ||
            isBrokerOnDeal(values?.brokerCommissions ?? [], userProfile.personKey);

        return show;
    };

    const filterVoucherList = () => {
        if (includes(["portlandBrokerAdmin"], userProfile.role) && !(values?.adminReviewType === "PORTLAND")) {
            return values?.vouchers?.filter((voucher) => {
                return voucher?.adminReviewType === "PORTLAND";
            });
        }
        return values?.vouchers;
    };

    const vouchers = filterVoucherList();

    const handleAddVoucherInstallment = async (dealKey) => {
        const result = await addVoucherInstallment(dealKey);
        if (result) {
            if (result.voucherKey) {
                navigate(`/voucher/${result.voucherKey}`);
            }
        }
    };

    const handleStartVoucher = async (dealKey) => {
        const result = await startVoucher(dealKey);
        if (result?.voucherKey) {
            navigate(`/voucher/${result.voucherKey}`);
        }
    };

    return (
        role !== "auditor" &&
        (values?.compStatus !== "Draft" && values?.compStatus !== null ? (
            <Paper style={{ marginTop: "2em", padding: "0 20px" }}>
                <StyledDivHeaderContainer>
                    <StyledDivHeader>
                        <div style={{ padding: "8px", boxSizing: "border-box" }}>
                            {vouchers?.at(-1)?.voucherStatus === "Closed" ? (
                                <StepperIconComplete />
                            ) : (
                                <StepperIcon text="2" fill="#2196f3" />
                            )}
                        </div>
                        <div style={{ padding: "8px" }}>
                            <h4>Voucher</h4>
                        </div>
                    </StyledDivHeader>

                    <div style={{ padding: "12px", boxSizing: "border-box" }}>
                        {includes(["broker", "evp", "portlandBrokerAdmin", "arizonaBrokerAdmin"], userProfile.role) &&
                        (values.totalInstallments === 1 || values.totalInstallments === null) &&
                        // Wait for API call to retrieve voucher data before rendering voucher section (see dealOperations-getDealSummary)
                        !isLoadingContent ? (
                            (values?.vouchers ?? []).length > 0 ? null : (
                                <Button
                                    disabled={userProfile.user === values?.auditCreatedBy ? false : true}
                                    variant="contained"
                                    color="primary"
                                    onClick={() => handleStartVoucher(values.dealKey)}
                                >
                                    Start
                                </Button>
                            )
                        ) : isLoadingContent ? null : (
                            includes(
                                ["broker", "evp", "portlandBrokerAdmin", "arizonaBrokerAdmin"],
                                userProfile.role
                            ) && (
                                <React.Fragment>
                                    {loading.addVoucherInstallment ? (
                                        <StyledProgressDiv>
                                            <CircularProgress size={20} color="primary" />
                                        </StyledProgressDiv>
                                    ) : (
                                        <Button
                                            onClick={() => handleAddVoucherInstallment(values.dealKey)}
                                            variant="contained"
                                            color="primary"
                                            disabled={
                                                isLoadingContent ||
                                                (values?.vouchers ?? []).length >= values?.totalInstallments
                                            }
                                        >
                                            <Add fill="#ffffff" />
                                            Add Installment
                                        </Button>
                                    )}
                                </React.Fragment>
                            )
                        )}
                    </div>
                </StyledDivHeaderContainer>
                {(values?.vouchers ?? []).length > 0 || isLoadingContent ? null : (
                    <div style={{ display: "flex", padding: "0 12px 12px 12px" }}>
                        <div>
                            <p>
                                Fill out your voucher information once you receive payment or proof of payment so you
                                can get paid in a timely fashion. Vouchers submitted by noon on Tuesday are paid out the
                                same week.
                            </p>
                        </div>
                    </div>
                )}
                {isLoadingContent
                    ? null
                    : showVoucherView() &&
                      vouchers?.length > 0 && (
                          <div style={{ display: "flex" }}>
                              <div style={{ flexGrow: 1, flexBasis: 0 }}>
                                  <Table>
                                      <TableHead>
                                          <TableRow>
                                              <TableCell>
                                                  <div
                                                      style={{
                                                          display: "flex",
                                                          alignItems: "center",
                                                      }}
                                                      onClick={() => handleSort("voucherKey")}
                                                  >
                                                      <span>Voucher</span>
                                                  </div>
                                              </TableCell>
                                              <TableCell>
                                                  <div
                                                      style={{
                                                          display: "flex",
                                                          alignItems: "center",
                                                      }}
                                                      onClick={() => handleSort("installmentNum")}
                                                  >
                                                      <StyledSortable>Installment</StyledSortable>
                                                      <Sorty />
                                                      {/* this table header is sortable */}
                                                  </div>
                                              </TableCell>
                                              <TableCell>
                                                  <div
                                                      style={{
                                                          display: "flex",
                                                          alignItems: "center",
                                                      }}
                                                      onClick={() => handleSort("datePaid")}
                                                  >
                                                      <span>Payment Date</span>
                                                  </div>
                                              </TableCell>
                                              <TableCell>
                                                  <div
                                                      style={{
                                                          display: "flex",
                                                          alignItems: "center",
                                                      }}
                                                      onClick={() => handleSort("myAmount")}
                                                  >
                                                      <StyledSortable>My Amount</StyledSortable>
                                                      <UpChevron />
                                                  </div>
                                              </TableCell>
                                              <TableCell>
                                                  <div
                                                      style={{
                                                          display: "flex",
                                                          alignItems: "center",
                                                      }}
                                                      onClick={() => handleSort("voucherStatus")}
                                                  >
                                                      <StyledSortable>Status</StyledSortable>
                                                      <DownChevron />
                                                  </div>
                                              </TableCell>
                                              <TableCell align="right"></TableCell>
                                          </TableRow>
                                      </TableHead>
                                      <TableBody>
                                          {vouchers &&
                                              orderBy(vouchers, columnToSort, sortDirection).map((x, i) => (
                                                  <TableRow key={`tr-${i}`} selectable="false">
                                                      <TableCell>
                                                          <StyledIdLink to={`/voucher/${x.voucherKey}`}>
                                                              {x?.voucherKey}
                                                          </StyledIdLink>
                                                      </TableCell>
                                                      <TableCell>
                                                          {!values.totalInstallments ||
                                                          (values?.totalInstallments ?? 1) === 1
                                                              ? "1 of 1"
                                                              : `${x?.installmentNum ?? ""} of ${
                                                                    values.totalInstallments
                                                                }`}
                                                      </TableCell>
                                                      <TableCell>
                                                          {get(
                                                              get(values, "installments", [{}])[
                                                                  (x?.installmentNum ?? 1) - 1
                                                              ],
                                                              "dueDate",
                                                              ""
                                                          )}
                                                      </TableCell>
                                                      <TableCell>
                                                          {toDollarAmountAcceptZero(
                                                              get(values, `myAmounts.${x.voucherKey}.myAmount`)
                                                          )}
                                                      </TableCell>
                                                      <TableCell>
                                                          <StatusChip status={x?.voucherStatus} />
                                                      </TableCell>
                                                      <TableCell
                                                          style={{
                                                              textAlign: "right",
                                                              paddingRight: "0px",
                                                          }}
                                                      >
                                                          {get(
                                                              compAction,
                                                              `${userProfile.role}.${x.voucherStatus}.buttonText`,
                                                              null
                                                          ) === "Start" ? (
                                                              <Button
                                                                  variant="contained"
                                                                  color="primary"
                                                                  onClick={() =>
                                                                      handleStartReviewingAsCorporateDataAndRedirect(
                                                                          values
                                                                      )
                                                                  }
                                                              >
                                                                  Start
                                                              </Button>
                                                          ) : get(
                                                                voucherAction,
                                                                `${userProfile.role}.${x.voucherStatus}`,
                                                                null
                                                            ) ? (
                                                              <StyledLb to={`/voucher/${x.voucherKey}`}>
                                                                  <Button
                                                                      variant={get(
                                                                          voucherAction,
                                                                          `${userProfile.role}.${x.voucherStatus}.variant`,
                                                                          null
                                                                      )}
                                                                      color="primary"
                                                                  >
                                                                      {get(
                                                                          compAction,
                                                                          `${userProfile.role}.${x.voucherStatus}.buttonText`,
                                                                          null
                                                                      )}
                                                                  </Button>
                                                              </StyledLb>
                                                          ) : (
                                                              <StyledLb to={`/voucher/${x.voucherKey}`}>
                                                                  <Button variant="outlined" color="primary">
                                                                      View
                                                                  </Button>
                                                              </StyledLb>
                                                          )}
                                                      </TableCell>
                                                  </TableRow>
                                              ))}
                                      </TableBody>
                                  </Table>
                              </div>
                          </div>
                      )}
            </Paper>
        ) : (
            // no comp status
            <div>
                <Paper style={{ marginTop: "2em", padding: "0 20px" }}>
                    <div style={{ display: "flex", alignItems: "center", margin: "-8px" }}>
                        <div style={{ padding: "8px" }}>
                            <StepperIcon text="2" fill="#cccccc" />
                        </div>
                        <div style={{ padding: "8px" }}>
                            <h4>Voucher</h4>
                        </div>
                    </div>
                    <div style={{ display: "flex", padding: "0 12px 12px 12px" }}>
                        <div>
                            <p>
                                Fill out your voucher information once you receive payment or proof of payment so you
                                can get paid in a timely fashion. Vouchers submitted by noon on Tuesday are paid out the
                                same week.
                            </p>
                        </div>
                    </div>
                </Paper>
            </div>
        ))
    );
};

export default DealSummaryVoucherTable;
